import { Spinner } from '@fluentui/react-components'
import { lazy, Suspense } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import AppShell from '@/components/layout/AppShell'
import { useAuthStore } from '@/stores/auth'
import { isAdmin } from '@/utils/auth'

// ==================== 懒加载页面 ====================
const Home = lazy(() => import('@/pages/Home'))
const EventsPlaza = lazy(() => import('@/pages/events/Plaza'))
const EventDetail = lazy(() => import('@/pages/events/Detail'))
const EventSchedule = lazy(() => import('@/pages/events/Schedule'))
const TeamsPlaza = lazy(() => import('@/pages/teams/Plaza'))
const TeamDetail = lazy(() => import('@/pages/teams/Detail'))
const TeamJoin = lazy(() => import('@/pages/teams/Join'))
const TeamCreate = lazy(() => import('@/pages/teams/Create'))
const ArticlesList = lazy(() => import('@/pages/articles/List'))
const ArticleDetail = lazy(() => import('@/pages/articles/Detail'))
const SearchPage = lazy(() => import('@/pages/Search'))
const Leaderboard = lazy(() => import('@/pages/Leaderboard'))
const Recruitments = lazy(() => import('@/pages/Recruitments'))
const Predictions = lazy(() => import('@/pages/Predictions'))
const Login = lazy(() => import('@/pages/auth/Login'))
const Register = lazy(() => import('@/pages/auth/Register'))
const ForgotPassword = lazy(() => import('@/pages/auth/ForgotPassword'))
const ResetPassword = lazy(() => import('@/pages/auth/ResetPassword'))
const CreateEventWizard = lazy(() => import('@/pages/events/CreateWizard'))
const MyEvents = lazy(() => import('@/pages/me/MyEvents'))
const ManageEvent = lazy(() => import('@/pages/manage/ManageEvent'))
const MessagesPage = lazy(() => import('@/pages/Messages'))
const ProfileLayout = lazy(() => import('@/pages/profile/ProfileLayout'))
const ProfileOverview = lazy(() => import('@/pages/profile/Overview'))
const ProfileEvents = lazy(() => import('@/pages/profile/MyEvents'))
const ProfileNotifications = lazy(() => import('@/pages/profile/Notifications'))
const ProfileAchievements = lazy(() => import('@/pages/profile/Achievements'))
const ProfileStats = lazy(() => import('@/pages/profile/Stats'))
const ProfileSubscription = lazy(() => import('@/pages/profile/Subscription'))
const ProfileSettings = lazy(() => import('@/pages/profile/Settings'))
const AdminLayout = lazy(() => import('@/pages/admin/AdminLayout'))
const AdminDashboard = lazy(() => import('@/pages/admin/Dashboard'))
const AdminUsers = lazy(() => import('@/pages/admin/Users'))
const AdminRoles = lazy(() => import('@/pages/admin/Roles'))
const AdminEvents = lazy(() => import('@/pages/admin/Events'))
const AdminTeams = lazy(() => import('@/pages/admin/Teams'))
const AdminSubscriptions = lazy(() => import('@/pages/admin/Subscriptions'))
const AdminBlacklists = lazy(() => import('@/pages/admin/Blacklists'))
const AdminArticles = lazy(() => import('@/pages/admin/Articles'))
const AdminAudit = lazy(() => import('@/pages/admin/Audit'))
const AdminFiles = lazy(() => import('@/pages/admin/Files'))
const AdminMetrics = lazy(() => import('@/pages/admin/Metrics'))
const NotFound = lazy(() => import('@/pages/NotFound'))

function Loading() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
      <Spinner label="页面加载中…" />
    </div>
  )
}

const W = ({ children }: { children: React.ReactNode }) => <Suspense fallback={<Loading />}>{children}</Suspense>

/** 需要登录的路由守卫 */
function RequireAuth({ children }: { children: React.ReactNode }) {
  const token = useAuthStore((s) => s.token)
  const location = useLocation()
  if (!token) {
    return <Navigate to={`/login?redirect=${encodeURIComponent(location.pathname + location.search)}`} replace />
  }
  return <>{children}</>
}

/** 需要管理员角色的路由守卫 */
function RequireAdmin({ children }: { children: React.ReactNode }) {
  const token = useAuthStore((s) => s.token)
  const user = useAuthStore((s) => s.user)
  const location = useLocation()
  if (!token) return <Navigate to={`/login?redirect=${encodeURIComponent(location.pathname)}`} replace />
  if (user && !isAdmin(user)) return <Navigate to="/" replace />
  return <>{children}</>
}

/** 未登录才能访问（登录/注册等） */
function GuestOnly({ children }: { children: React.ReactNode }) {
  const token = useAuthStore((s) => s.token)
  if (token) return <Navigate to="/" replace />
  return <>{children}</>
}

export default function AppRouter() {
  return (
    <Routes>
      {/* ============ 认证页（无外壳） ============ */}
      <Route
        path="/login"
        element={
          <W>
            <GuestOnly>
              <Login />
            </GuestOnly>
          </W>
        }
      />
      <Route
        path="/register"
        element={
          <W>
            <GuestOnly>
              <Register />
            </GuestOnly>
          </W>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <W>
            <GuestOnly>
              <ForgotPassword />
            </GuestOnly>
          </W>
        }
      />
      <Route
        path="/reset-password"
        element={
          <W>
            <ResetPassword />
          </W>
        }
      />

      {/* ============ 应用外壳 ============ */}
      <Route element={<AppShell />}>
        <Route
          path="/"
          element={
            <W>
              <Home />
            </W>
          }
        />

        {/* 赛事 */}
        <Route
          path="/events"
          element={
            <W>
              <EventsPlaza />
            </W>
          }
        />
        <Route
          path="/events/:id"
          element={
            <W>
              <EventDetail />
            </W>
          }
        />
        <Route
          path="/events/:id/schedule"
          element={
            <W>
              <EventSchedule />
            </W>
          }
        />
        <Route
          path="/events/create"
          element={
            <W>
              <RequireAuth>
                <CreateEventWizard />
              </RequireAuth>
            </W>
          }
        />

        {/* 战队 */}
        <Route
          path="/teams"
          element={
            <W>
              <TeamsPlaza />
            </W>
          }
        />
        <Route
          path="/teams/join/:token"
          element={
            <W>
              <TeamJoin />
            </W>
          }
        />
        <Route
          path="/teams/create"
          element={
            <W>
              <RequireAuth>
                <TeamCreate />
              </RequireAuth>
            </W>
          }
        />
        <Route
          path="/teams/:id"
          element={
            <W>
              <TeamDetail />
            </W>
          }
        />

        {/* 资讯 / 搜索 / 排行 / 招募 / 预测 */}
        <Route
          path="/articles"
          element={
            <W>
              <ArticlesList />
            </W>
          }
        />
        <Route
          path="/articles/:id"
          element={
            <W>
              <ArticleDetail />
            </W>
          }
        />
        <Route
          path="/search"
          element={
            <W>
              <SearchPage />
            </W>
          }
        />
        <Route
          path="/leaderboard"
          element={
            <W>
              <Leaderboard />
            </W>
          }
        />
        <Route
          path="/recruitments"
          element={
            <W>
              <Recruitments />
            </W>
          }
        />
        <Route
          path="/predictions"
          element={
            <W>
              <Predictions />
            </W>
          }
        />

        {/* 我的 */}
        <Route
          path="/my/events"
          element={
            <W>
              <RequireAuth>
                <MyEvents />
              </RequireAuth>
            </W>
          }
        />
        <Route
          path="/manage/events/:id"
          element={
            <W>
              <RequireAuth>
                <ManageEvent />
              </RequireAuth>
            </W>
          }
        />
        <Route
          path="/messages"
          element={
            <W>
              <RequireAuth>
                <MessagesPage />
              </RequireAuth>
            </W>
          }
        />
        <Route
          path="/profile"
          element={
            <RequireAuth>
              <ProfileLayout />
            </RequireAuth>
          }
        >
          <Route
            index
            element={
              <W>
                <ProfileOverview />
              </W>
            }
          />
          <Route
            path="events"
            element={
              <W>
                <ProfileEvents />
              </W>
            }
          />
          <Route
            path="notifications"
            element={
              <W>
                <ProfileNotifications />
              </W>
            }
          />
          <Route
            path="achievements"
            element={
              <W>
                <ProfileAchievements />
              </W>
            }
          />
          <Route
            path="stats"
            element={
              <W>
                <ProfileStats />
              </W>
            }
          />
          <Route
            path="subscription"
            element={
              <W>
                <ProfileSubscription />
              </W>
            }
          />
          <Route
            path="settings"
            element={
              <W>
                <ProfileSettings />
              </W>
            }
          />
        </Route>

        {/* 管理后台 */}
        <Route
          path="/admin"
          element={
            <RequireAdmin>
              <AdminLayout />
            </RequireAdmin>
          }
        >
          <Route
            index
            element={
              <W>
                <AdminDashboard />
              </W>
            }
          />
          <Route
            path="users"
            element={
              <W>
                <AdminUsers />
              </W>
            }
          />
          <Route
            path="roles"
            element={
              <W>
                <AdminRoles />
              </W>
            }
          />
          <Route
            path="events"
            element={
              <W>
                <AdminEvents />
              </W>
            }
          />
          <Route
            path="teams"
            element={
              <W>
                <AdminTeams />
              </W>
            }
          />
          <Route
            path="subscriptions"
            element={
              <W>
                <AdminSubscriptions />
              </W>
            }
          />
          <Route
            path="blacklists"
            element={
              <W>
                <AdminBlacklists />
              </W>
            }
          />
          <Route
            path="articles"
            element={
              <W>
                <AdminArticles />
              </W>
            }
          />
          <Route
            path="audit"
            element={
              <W>
                <AdminAudit />
              </W>
            }
          />
          <Route
            path="files"
            element={
              <W>
                <AdminFiles />
              </W>
            }
          />
          <Route
            path="metrics"
            element={
              <W>
                <AdminMetrics />
              </W>
            }
          />
        </Route>

        <Route
          path="*"
          element={
            <W>
              <NotFound />
            </W>
          }
        />
      </Route>
    </Routes>
  )
}
